import API_BASE from '../../utils/api';
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../../components/Sidebar';
import ProfileDropdown from '../../components/ProfileDropdown';
import UpcomingEventsScroller from '../../components/UpcomingEventsScroller';
import { useLicense } from '../../context/LicenseContext';
import { SkeletonStatCard, SkeletonBanner, SkeletonTable } from '../../components/Skeleton';
import FinanceOverviewWidget from '../../components/FinanceOverviewWidget';
import StudentGrowthWidget from '../../components/StudentGrowthWidget';
import PassRateByYearWidget from '../../components/PassRateByYearWidget';

const PrincipalDashboard = () => {
  const { user } = useAuth();
  const { isModuleEnabled } = useLicense();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [classData, setClassData] = useState([]);
  const [teacherStatus, setTeacherStatus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('classes');

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/attendance/dashboard-stats`);
      setOverview(res.data.overview);
      setClassData(res.data.classWiseAttendance);
      setTeacherStatus(res.data.teacherStatus);
    } catch (err) {
      console.error('Error fetching dashboard stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const o = overview || {};

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <nav className="bg-white shadow-sm sticky top-0 z-10">
          <div className="px-6 py-4 flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-semibold text-gray-800">Principal Dashboard</h1>
              <p className="text-sm text-gray-500">{new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
            </div>
            <div className="flex items-center gap-4">
              <button onClick={fetchDashboardStats} className="text-indigo-600 hover:text-indigo-800 text-sm font-medium flex items-center gap-1">
                🔄 Refresh
              </button>
              <ProfileDropdown />
            </div>
          </div>
        </nav>

        <div className="p-6">
          <UpcomingEventsScroller />
          {/* Top Stats Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {loading ? (
              Array.from({ length: 4 }).map((_, i) => <SkeletonStatCard key={i} />)
            ) : (
              <>
                <div className="bg-white rounded-lg shadow p-5 cursor-pointer hover:shadow-lg transition" onClick={() => navigate('/principal/students')}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-500 text-xs font-medium uppercase">Total Students</p>
                      <p className="text-3xl font-bold mt-1">{o.totalStudents}</p>
                    </div>
                    <div className="p-3 rounded-full bg-blue-100"><span className="text-2xl">👥</span></div>
                  </div>
                </div>
                <div className="bg-white rounded-lg shadow p-5 cursor-pointer hover:shadow-lg transition" onClick={() => navigate('/principal/teachers')}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-500 text-xs font-medium uppercase">Total Teachers</p>
                      <p className="text-3xl font-bold mt-1">{o.totalTeachers}</p>
                    </div>
                    <div className="p-3 rounded-full bg-green-100"><span className="text-2xl">👨‍🏫</span></div>
                  </div>
                </div>
                {isModuleEnabled('classes') && (
                  <div className="bg-white rounded-lg shadow p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-500 text-xs font-medium uppercase">Total Classes</p>
                        <p className="text-3xl font-bold mt-1">{o.totalClasses}</p>
                      </div>
                      <div className="p-3 rounded-full bg-purple-100"><span className="text-2xl">🏫</span></div>
                    </div>
                  </div>
                )}
                {isModuleEnabled('leaves') && (
                  <div className="bg-white rounded-lg shadow p-5 cursor-pointer hover:shadow-lg transition" onClick={() => navigate('/principal/leaves')}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-500 text-xs font-medium uppercase">Pending Leaves</p>
                        <p className="text-3xl font-bold mt-1">{o.pendingLeaves}</p>
                      </div>
                      <div className="p-3 rounded-full bg-yellow-100"><span className="text-2xl">📅</span></div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Today's Live Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {loading ? (
              Array.from({ length: 3 }).map((_, i) => <SkeletonBanner key={i} />)
            ) : (
              <>
                {/* Students Present */}
                <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg shadow-lg p-6 text-white">
                  <h3 className="text-sm font-medium opacity-90 uppercase">Students Present Today</h3>
                  <div className="flex items-end justify-between mt-2">
                    <div>
                      <p className="text-4xl font-bold">{o.studentsPresent}</p>
                      <p className="text-sm opacity-80">out of {o.attendanceMarked} marked</p>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-bold">{o.attendancePercentage}%</p>
                      <p className="text-xs opacity-80">attendance</p>
                    </div>
                  </div>
                  <div className="mt-3 bg-white bg-opacity-20 rounded-full h-2">
                    <div className="bg-white rounded-full h-2 transition-all" style={{ width: `${o.attendancePercentage}%` }}></div>
                  </div>
                </div>

                {/* Teachers Present */}
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg shadow-lg p-6 text-white">
                  <h3 className="text-sm font-medium opacity-90 uppercase">Teachers Present Today</h3>
                  <div className="flex items-end justify-between mt-2">
                    <div>
                      <p className="text-4xl font-bold">{o.teachersPresent}</p>
                      <p className="text-sm opacity-80">out of {o.totalTeachers}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-bold">{o.totalTeachers > 0 ? Math.round((o.teachersPresent / o.totalTeachers) * 100) : 0}%</p>
                      <p className="text-xs opacity-80">present</p>
                    </div>
                  </div>
                  <div className="mt-3 bg-white bg-opacity-20 rounded-full h-2">
                    <div className="bg-white rounded-full h-2 transition-all" style={{ width: `${o.totalTeachers > 0 ? Math.round((o.teachersPresent / o.totalTeachers) * 100) : 0}%` }}></div>
                  </div>
                </div>

                {/* Fees */}
                <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg shadow-lg p-6 text-white">
                  <h3 className="text-sm font-medium opacity-90 uppercase">Fees Overview</h3>
                  <div className="mt-2">
                    <p className="text-2xl font-bold">₹{(o.feesCollected || 0).toLocaleString()}</p>
                    <p className="text-sm opacity-80">collected</p>
                  </div>
                  <div className="mt-2 flex justify-between text-sm">
                    <span>Pending: ₹{(o.feesPending || 0).toLocaleString()}</span>
                    <span>Overdue: ₹{(o.feesOverdue || 0).toLocaleString()}</span>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Tabs: Class Report / Teacher Status */}
          <div className="bg-white rounded-lg shadow">
            <div className="border-b px-6 pt-4 flex gap-6">
              <button
                onClick={() => setActiveTab('classes')}
                className={`pb-3 px-1 font-semibold text-sm border-b-2 transition ${activeTab === 'classes' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
              >
                📋 Class-wise Attendance Report
              </button>
              <button
                onClick={() => setActiveTab('teachers')}
                className={`pb-3 px-1 font-semibold text-sm border-b-2 transition ${activeTab === 'teachers' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
              >
                👨‍🏫 Teacher Status
              </button>
            </div>

            {loading && <SkeletonTable rows={5} cols={10} />}

            {/* Class-wise Attendance Table */}
            {!loading && activeTab === 'classes' && (
              <div className="p-6 overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Class</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Class Teacher</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Total</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Present</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Absent</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Late</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">%</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Marked At</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {classData.length > 0 ? classData.map((cls, i) => (
                      <tr key={cls.classId} className={`hover:bg-gray-50 ${!cls.attendanceMarked ? 'bg-red-50' : ''}`}>
                        <td className="px-4 py-3 text-sm text-gray-500">{i + 1}</td>
                        <td className="px-4 py-3 font-medium">{cls.className}-{cls.section}</td>
                        <td className="px-4 py-3 text-sm">{cls.classTeacher}</td>
                        <td className="px-4 py-3 text-center font-medium">{cls.totalStudents}</td>
                        <td className="px-4 py-3 text-center">
                          <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-semibold">{cls.present}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-semibold">{cls.absent}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-semibold">{cls.late}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`font-bold ${cls.percentage >= 80 ? 'text-green-600' : cls.percentage >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>
                            {cls.attendanceMarked ? `${cls.percentage}%` : '-'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center text-sm">{cls.markedAt}</td>
                        <td className="px-4 py-3 text-center">
                          {cls.attendanceMarked ? (
                            <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-semibold">✅ Done</span>
                          ) : (
                            <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-semibold">⏳ Pending</span>
                          )}
                        </td>
                      </tr>
                    )) : (
                      <tr><td colSpan="10" className="px-4 py-8 text-center text-gray-500">No classes found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Teacher Status Table */}
            {!loading && activeTab === 'teachers' && (
              <div className="p-6 overflow-x-auto">
                <div className="mb-4 flex gap-4 text-sm">
                  <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full font-medium">
                    Present: {teacherStatus.filter(t => t.present).length}
                  </span>
                  <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full font-medium">
                    Not Marked: {teacherStatus.filter(t => !t.present).length}
                  </span>
                </div>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Teacher Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned Class</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Login Time</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Selfie Check-in</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {teacherStatus.length > 0 ? teacherStatus.map((t, i) => (
                      <tr key={t._id} className={`hover:bg-gray-50 ${!t.present ? 'bg-red-50' : ''}`}>
                        <td className="px-4 py-3 text-sm text-gray-500">{i + 1}</td>
                        <td className="px-4 py-3 font-medium">{t.name}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{t.email}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{t.phone}</td>
                        <td className="px-4 py-3 text-sm">{t.assignedClass}</td>
                        <td className="px-4 py-3 text-center text-sm">{t.loginTime || '-'}</td>
                        <td className="px-4 py-3 text-center text-sm">
                          {t.selfieMarked ? (
                            <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-semibold">📸 {t.selfieAttendanceAt} ({t.distanceFromSchool}m)</span>
                          ) : (
                            <span className="bg-gray-100 text-gray-500 px-2 py-1 rounded-full text-xs">Not done</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {t.present ? (
                            <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-semibold">✅ Present</span>
                          ) : (
                            <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-xs font-semibold">❌ Absent</span>
                          )}
                        </td>
                      </tr>
                    )) : (
                      <tr><td colSpan="8" className="px-4 py-8 text-center text-gray-500">No teachers found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Analytics */}
          <div className="mt-6 space-y-6">
            <FinanceOverviewWidget />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <StudentGrowthWidget />
              <PassRateByYearWidget />
            </div>
          </div>

          {/* Quick Actions */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { module: 'leaves', path: '/principal/leaves', icon: '📅', title: 'Review Leaves', desc: `${o.pendingLeaves} pending requests` },
              { module: 'attendance', path: '/principal/attendance', icon: '📋', title: 'View Attendance', desc: "Today's detailed view" },
              { module: 'fees', path: '/principal/fees', icon: '💰', title: 'Manage Fees', desc: `₹${(o.feesPending || 0).toLocaleString()} pending` },
            ].filter(item => isModuleEnabled(item.module)).map(item => (
              <button key={item.path} onClick={() => navigate(item.path)} className="p-4 bg-white rounded-lg shadow hover:shadow-lg transition flex items-center space-x-3">
                <span className="text-3xl">{item.icon}</span>
                <div className="text-left">
                  <h3 className="font-semibold">{item.title}</h3>
                  <p className="text-sm text-gray-500">{item.desc}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrincipalDashboard;